import { useState, useEffect } from 'react';
import { createRoute, redirect, Link } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { z } from 'zod';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertTriangle, Search, HelpCircle } from 'lucide-react';
import { Route as rootRoute } from '../__root';
import { Layout } from '@/client/layouts/layout';
import { Input } from '@/client/components/ui/input';
import { Button } from '@/client/components/ui/button';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/client/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/client/components/ui/table';
import { callCrudApi } from '@/client/utils/crud';
import { useClientsStore } from '@/client/store/clientsStore';
import { API_BASE_URL } from '@/client/config';
import { EVENT_STATUS_NAMES } from '@shared/types/events';
import type { EventDiagnosticResponse, EventDiagnosticCheck } from '@shared/types/events';
import type { UpdateEventStatusPayload, UpdateEventStatusResponse } from '@shared/types/crud';

/** Statuses safe to force via this utility (production use cases only) */
const ALLOWED_TARGET_STATUSES = [4, 7] as const;

const lookupSchema = z.object({
  clientID: z.string().length(10, 'Client ID must be 10 digits').regex(/^\d+$/, 'Client ID must be a number'),
  eventID: z.string().min(1, 'Event ID is required'),
});

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'utilities/update-event-status',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: UpdateEventStatusComponent,
});

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------

function CheckIcon({ status }: { status: EventDiagnosticCheck['status'] }) {
  switch (status) {
    case 'pass':
      return <CheckCircle2 className="size-4 text-green-600" />;
    case 'fail':
      return <XCircle className="size-4 text-red-600" />;
    case 'warn':
      return <AlertTriangle className="size-4 text-amber-500" />;
    case 'not_found':
      return <HelpCircle className="size-4 text-muted-foreground" />;
  }
}

function RequestPreview({ path, payload }: { path: string; payload: Record<string, unknown> | null }) {
  if (!payload) return null;
  return (
    <div className="rounded bg-muted p-3 font-mono text-xs space-y-1">
      <div className="text-muted-foreground">POST {path}</div>
      <pre className="text-foreground">{JSON.stringify(payload, null, 2)}</pre>
    </div>
  );
}

function DiagnosticCard({ check, fields, expectedField }: {
  check: EventDiagnosticCheck;
  fields?: { label: string; value: string | null }[];
  /** Which field label should show the expected badge when check isn't passing */
  expectedField?: string;
}) {
  return (
    <div className="border rounded-lg p-3 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2 text-sm">
        <CheckIcon status={check.status} />
        <span className="font-medium">{check.label}</span>
      </div>
      {/* Fields */}
      {fields && fields.length > 0 && (
        <div className="ml-6 space-y-0.5">
          {fields.map((f) => (
            <div key={f.label} className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground w-28 shrink-0">{f.label}:</span>
              <span className="font-mono break-all">{f.value ?? '—'}</span>
              {f.label === expectedField && check.expected && check.status !== 'pass' && (
                <span className="inline-flex items-center rounded-md bg-red-500/10 px-1.5 py-0.5 text-[11px] font-medium text-red-500 ring-1 ring-inset ring-red-500/20">
                  expected: {check.expected}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      {/* Not found */}
      {check.status === 'not_found' && !fields?.length && (
        <div className="ml-6 text-xs text-muted-foreground italic">not found</div>
      )}
    </div>
  );
}

function StatusSelect({ value, onChange, disabled, id }: {
  value: number | null;
  onChange: (v: number) => void;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <Select
      value={value?.toString() ?? ''}
      onValueChange={(val) => onChange(Number(val))}
      disabled={disabled}
    >
      <SelectTrigger id={id}>
        <SelectValue placeholder="Select target status" />
      </SelectTrigger>
      <SelectContent>
        {ALLOWED_TARGET_STATUSES.map((idx) => (
          <SelectItem key={idx} value={idx.toString()}>
            <span className="capitalize">{EVENT_STATUS_NAMES[idx]}</span>
            <span className="text-muted-foreground ml-2">({idx})</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function UpdateEventStatusComponent() {
  const { getAccessTokenSilently, logout } = useAuth0();

  // Form state
  const [clientID, setClientID] = useState('');
  const [eventID, setEventID] = useState('');
  const [eventStatus, setEventStatus] = useState<number | null>(null);

  // Flow state
  const [diagnosing, setDiagnosing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [diagnostic, setDiagnostic] = useState<EventDiagnosticResponse | null>(null);
  const [diagnosticOverride, setDiagnosticOverride] = useState(false);

  // Client picker
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [clientFilter, setClientFilter] = useState('');
  const { clients, fetchClients, loading: clientsLoading } = useClientsStore();

  useEffect(() => {
    const loadClients = async () => {
      const token = await getAccessTokenSilently();
      await fetchClients(token, logout);
    };
    if (clients.length === 0) {
      loadClients();
    }
  }, [getAccessTokenSilently, fetchClients, clients.length]);

  const activeClients = clients
    .filter((client) => client.active === 1)
    .filter((client) =>
      clientFilter === '' ||
      client.name.toLowerCase().includes(clientFilter.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const selectedClientName = clients.find((c) => c.clientID === clientID)?.name;

  // Derived state
  const allChecksPassed = diagnostic?.checks.every((c) => c.status === 'pass') ?? false;
  const currentStatus = diagnostic?.eventStatus?.eventStatus ?? null;
  const isPublishedAndHealthy = allChecksPassed && currentStatus === 7;
  const hasActiveLiveStream = diagnostic?.liveStream != null && diagnostic.liveStream.status !== 'terminated';
  const isCurrentlyPublishing = currentStatus === 6;
  const isActiveProcess = hasActiveLiveStream || isCurrentlyPublishing;
  const isTargetSameAsCurrent = eventStatus !== null && currentStatus !== null && eventStatus === currentStatus;
  const isPublishWithoutPrereqs = eventStatus === 7 && diagnostic != null && (
    !diagnostic.transcodeJob || diagnostic.transcodeJob.progress !== 100 || !diagnostic.mediaAsset
  );
  const updateUnlocked = !isPublishedAndHealthy && !isActiveProcess && (allChecksPassed || diagnosticOverride);
  const canDiagnose = !diagnosing && !updating && clientID && eventID;
  const canUpdate = !updating && !diagnosing && updateUnlocked && eventStatus !== null && clientID && eventID
    && !isTargetSameAsCurrent && !isPublishWithoutPrereqs;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const resetFlow = () => {
    setDiagnostic(null);
    setDiagnosticOverride(false);
    setEventStatus(null);
  };

  const handleClientSelect = (selectedClientID: string) => {
    setClientID(selectedClientID);
    setClientDialogOpen(false);
    setClientFilter('');
    resetFlow();
  };

  const handleClientIDChange = (val: string) => {
    setClientID(val);
    resetFlow();
  };

  const handleEventIDChange = (val: string) => {
    setEventID(val);
    resetFlow();
  };

  const handleDiagnose = async () => {
    const validation = lookupSchema.safeParse({ clientID, eventID });
    if (!validation.success) {
      toast.error(validation.error.issues[0].message);
      return;
    }

    setDiagnosing(true);
    setDiagnostic(null);
    setDiagnosticOverride(false);

    try {
      const accessToken = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/events/${clientID}/${eventID}/status-diagnostic`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Diagnostic failed: ${response.statusText}`);
      }

      const data: EventDiagnosticResponse = await response.json();
      setDiagnostic(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to run diagnostic');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleUpdate = async () => {
    if (eventStatus === null) return;

    setUpdating(true);

    try {
      const accessToken = await getAccessTokenSilently();
      const payload: UpdateEventStatusPayload = { clientID, eventID, eventStatus };
      const response = await callCrudApi('/events/update', payload, accessToken);

      const crudResponse = response.data as unknown as UpdateEventStatusResponse;

      if (crudResponse?.errors?.hasError) {
        toast.error(crudResponse.errors.message || 'CRUD API returned an error');
      } else if (crudResponse?.data) {
        toast.success(`Event ${crudResponse.data.eventID} → ${crudResponse.data.eventStatus}`, {
          description: crudResponse.data.title || undefined,
        });
        setDiagnostic(null);
        setEventStatus(null);
        setDiagnosticOverride(false);
        handleDiagnose();
      } else {
        const directResponse = response as unknown as UpdateEventStatusResponse;
        if (directResponse?.errors?.hasError === false && directResponse?.data) {
          toast.success(`Event ${directResponse.data.eventID} → ${directResponse.data.eventStatus}`, {
            description: directResponse.data.title || undefined,
          });
          setDiagnostic(null);
          setEventStatus(null);
          setDiagnosticOverride(false);
          handleDiagnose();
        } else {
          toast.error('Unexpected response format');
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setUpdating(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Layout>
      <div className="space-y-6">
        {/* ---------------------------------------------------------------- */}
        {/* Step 1 — Identify the event                                      */}
        {/* ---------------------------------------------------------------- */}
        <section>
          <h3 className="text-2xl font-semibold mb-1">Update Event Status</h3>
          <p className="text-muted-foreground mb-6 text-sm">
            Force an event into a specific status via the CRUD API.
            Diagnose first to confirm the event is stuck, then apply the change.
          </p>

          <div className="max-w-md space-y-4">
            {/* Client ID */}
            <div className="space-y-2">
              <Label htmlFor="clientID">Client ID</Label>
              <div className="flex gap-2">
                <Input
                  id="clientID"
                  type="text"
                  value={clientID}
                  onChange={(e) => handleClientIDChange(e.target.value)}
                  placeholder="Enter client ID"
                  required
                  disabled={diagnosing || updating}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setClientDialogOpen(true)}
                  disabled={diagnosing || updating}
                >
                  Browse
                </Button>
              </div>
              {selectedClientName && (
                <p className="text-sm text-muted-foreground">{selectedClientName}</p>
              )}
            </div>

            {/* Event ID */}
            <div className="space-y-2">
              <Label htmlFor="eventID">Event ID</Label>
              <Input
                id="eventID"
                type="text"
                value={eventID}
                onChange={(e) => handleEventIDChange(e.target.value)}
                placeholder="e.g. 2026021263"
                required
                disabled={diagnosing || updating}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Event ID only (without client ID prefix)
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <Button
                onClick={handleDiagnose}
                disabled={!canDiagnose}
                variant="secondary"
              >
                <Search className="size-4 mr-2" />
                {diagnosing ? 'Diagnosing...' : 'Diagnose'}
              </Button>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------------- */}
        {/* Path B — Diagnostic results                                      */}
        {/* ---------------------------------------------------------------- */}
        {diagnostic && (
          <section className="space-y-4">
            <h4 className="font-semibold">Pre-flight Checks</h4>

            <div className="space-y-2">
              {/* Event Status */}
              <DiagnosticCard
                check={diagnostic.checks.find((c) => c.label === 'Event status record')!}
                expectedField="eventStatus"
                fields={diagnostic.eventStatus ? [
                  { label: 'eventStatus', value: `${diagnostic.eventStatus.eventStatusName} (${diagnostic.eventStatus.eventStatus})` },
                  { label: 'published', value: String(diagnostic.eventStatus.published) },
                  { label: 'lastError', value: diagnostic.eventStatus.lastError },
                ] : undefined}
              />

              {/* Live Stream */}
              <DiagnosticCard
                check={diagnostic.checks.find((c) => c.label === 'Live stream terminated')!}
                expectedField="status"
                fields={diagnostic.liveStream ? [
                  { label: 'liveID', value: diagnostic.liveStream.liveID },
                  { label: 'status', value: diagnostic.liveStream.status },
                  { label: 'encoderID', value: diagnostic.liveStream.encoderID },
                  { label: 'startDateTime', value: diagnostic.liveStream.startDateTime },
                  { label: 'endDateTime', value: diagnostic.liveStream.endDateTime },
                ] : undefined}
              />

              {/* Transcode Job */}
              <DiagnosticCard
                check={diagnostic.checks.find((c) => c.label === 'Transcode job complete')!}
                expectedField="progress"
                fields={diagnostic.transcodeJob ? [
                  { label: 'id', value: diagnostic.transcodeJob.id },
                  { label: 'progress', value: `${diagnostic.transcodeJob.progress}%` },
                  { label: 'status', value: diagnostic.transcodeJob.status },
                  { label: 'jobType', value: diagnostic.transcodeJob.jobType },
                  { label: 'createdAt', value: diagnostic.transcodeJob.createdAt },
                ] : undefined}
              />

              {/* Media Asset */}
              <DiagnosticCard
                check={diagnostic.checks.find((c) => c.label === 'Live recording asset')!}
                expectedField="currentStatus"
                fields={diagnostic.mediaAsset ? [
                  { label: 'assetID', value: diagnostic.mediaAsset.assetID },
                  { label: 'currentStatus', value: diagnostic.mediaAsset.currentStatus },
                  { label: 'assetType', value: diagnostic.mediaAsset.assetType },
                  { label: 'fileSize', value: diagnostic.mediaAsset.fileSize ? `${(Number(diagnostic.mediaAsset.fileSize) / 1_000_000).toFixed(1)} MB` : null },
                  { label: 'path', value: diagnostic.mediaAsset.path },
                  { label: 'liveID', value: diagnostic.mediaAsset.liveID },
                ] : undefined}
              />
            </div>

            {/* Update form — gated by check results */}
            <div className="border-t pt-4 space-y-3">
              {isActiveProcess ? (
                <div>
                  <h4 className="font-semibold flex items-center gap-2 text-sm text-red-500">
                    <XCircle className="size-4" />
                    {hasActiveLiveStream ? 'Active live stream — cannot modify' : 'Event is publishing — cannot modify'}
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {hasActiveLiveStream
                      ? `Live stream status is "${diagnostic?.liveStream?.status}". Wait for the stream to terminate before changing event status.`
                      : 'The event is currently being published. Wait for publishing to complete before changing status.'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {hasActiveLiveStream && (
                      <>
                        <Link
                          to="/events/$eventId"
                          params={{ eventId: `${clientID}_${eventID}` }}
                          className="font-medium text-foreground underline hover:text-primary"
                        >
                          View event detail
                        </Link>
                        {' · '}
                      </>
                    )}
                    If this process appears stuck, check the{' '}
                    <span className="font-medium text-foreground">liveStreams</span> and{' '}
                    <span className="font-medium text-foreground">eventStatus</span> tables in the database,
                    review the <span className="font-medium text-foreground">Stalled Publish runbook</span>,
                    or consult the team before forcing a status change.
                  </p>
                </div>
              ) : isPublishedAndHealthy ? (
                <div>
                  <h4 className="font-semibold flex items-center gap-2 text-sm">
                    <CheckCircle2 className="size-4 text-green-600" />
                    All checks passed — event is published
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    This event is published with valid data. If a status change is truly needed,
                    update the <span className="font-medium text-foreground">eventStatus</span> table directly
                    in the database and coordinate with the team.
                  </p>
                </div>
              ) : allChecksPassed ? (
                <h4 className="font-semibold flex items-center gap-2 text-sm">
                  <CheckCircle2 className="size-4 text-green-600" />
                  All checks passed — ready to update
                </h4>
              ) : (
                <div>
                  <h4 className="font-semibold flex items-center gap-2 text-sm text-amber-500">
                    <AlertTriangle className="size-4" />
                    Some checks did not pass
                  </h4>
                  {!diagnosticOverride && (
                    <button
                      type="button"
                      className="text-xs text-muted-foreground hover:text-foreground underline mt-1"
                      onClick={() => setDiagnosticOverride(true)}
                    >
                      I understand the risks — unlock update
                    </button>
                  )}
                </div>
              )}

              {updateUnlocked && (
                <div className="max-w-md space-y-3">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1 space-y-2">
                      <Label htmlFor="eventStatus">New Status</Label>
                      <StatusSelect value={eventStatus} onChange={setEventStatus} disabled={updating} id="eventStatus" />
                    </div>
                    <Button onClick={handleUpdate} disabled={!canUpdate}>
                      {updating ? 'Updating...' : 'Update Status'}
                    </Button>
                  </div>
                  {isTargetSameAsCurrent && (
                    <p className="text-xs text-amber-500">Event is already in this status.</p>
                  )}
                  {isPublishWithoutPrereqs && (
                    <p className="text-xs text-red-500">
                      Cannot set to published — requires a completed transcode job and a media asset.
                      Check the <span className="font-medium">transcodeJobs</span> and{' '}
                      <span className="font-medium">mediaassets</span> tables if you believe this is incorrect.
                    </p>
                  )}
                  <RequestPreview
                    path="/events/update"
                    payload={eventStatus !== null ? { clientID, eventID, eventStatus } : null}
                  />
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {/* Client Selection Dialog */}
      <Dialog open={clientDialogOpen} onOpenChange={(open) => {
        setClientDialogOpen(open);
        if (!open) setClientFilter('');
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle>Select Client</DialogTitle>
            <DialogDescription>
              Choose an active client from the list below.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="text"
            placeholder="Filter by client name..."
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            className="mb-2"
          />
          <div className="overflow-auto flex-1">
            {clientsLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                Loading clients...
              </div>
            ) : activeClients.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No active clients found.
              </div>
            ) : (
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-32">Client ID</TableHead>
                    <TableHead>Name</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeClients.map((client) => (
                    <TableRow
                      key={client.clientID}
                      className="cursor-pointer hover:bg-muted"
                      onClick={() => handleClientSelect(client.clientID)}
                    >
                      <TableCell className="font-mono">{client.clientID}</TableCell>
                      <TableCell>{client.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
